import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { cn } from "~/lib/utils";

import { DashboardsPageFrame } from "./dashboardKit";
import {
  buildDataDictionaryErdFrameUrl,
  dataDictionarySchemaQueryOptions,
  isDataDictionaryErdFrameCrossOrigin,
} from "./dataDictionaryReactQuery";
import type { DataDictionaryTable } from "./dataDictionaryTypes";

type DictionaryFilter = "all" | "carespace" | "needs-definition";
type DictionaryView = "diagram" | "definitions";

export function DataDictionaryDashboard() {
  const schemaQuery = useQuery(dataDictionarySchemaQueryOptions());
  const [view, setView] = useState<DictionaryView>("diagram");
  const erdFrameUrl = schemaQuery.data ? buildDataDictionaryErdFrameUrl(schemaQuery.data) : null;
  const canSandboxErd = erdFrameUrl ? isDataDictionaryErdFrameCrossOrigin(erdFrameUrl) : false;

  return (
    <DashboardsPageFrame fill className="gap-3">
      <header className="shrink-0 rounded-lg border border-border/70 bg-background/80 px-4 py-3 shadow-xs">
        <div className="flex flex-col justify-between gap-4 xl:flex-row xl:items-end">
          <div>
            <p className="mb-1 text-[0.7rem] font-bold tracking-[0.08em] text-blue-600 uppercase">
              Product reference · live from production
            </p>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Data Dictionary
            </h1>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
              Follow the Carespace ID through the schema, read definitions where the data is
              created, or explore the full relationship map. Content refreshes from the exact
              deployed schema.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {schemaQuery.data ? (
              <>
                <CoverageSummary
                  label="Tables defined"
                  documented={schemaQuery.data.catalog.coverage.tables.documented}
                  total={schemaQuery.data.catalog.coverage.tables.total}
                  percent={schemaQuery.data.catalog.coverage.tables.percent}
                />
                <CoverageSummary
                  label="Columns defined"
                  documented={schemaQuery.data.catalog.coverage.columns.documented}
                  total={schemaQuery.data.catalog.coverage.columns.total}
                  percent={schemaQuery.data.catalog.coverage.columns.percent}
                />
                <a
                  className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 no-underline"
                  href={`https://github.com/${schemaQuery.data.repository}/blob/${schemaQuery.data.sha}/${schemaQuery.data.schemaPath}`}
                  rel="noreferrer"
                  target="_blank"
                >
                  Production · {schemaQuery.data.shortSha}
                </a>
              </>
            ) : null}
          </div>
        </div>

        <div className="mt-3 flex w-fit rounded-md border border-border bg-muted/50 p-0.5">
          <ViewButton active={view === "diagram"} onClick={() => setView("diagram")}>
            Schema map
          </ViewButton>
          <ViewButton active={view === "definitions"} onClick={() => setView("definitions")}>
            Definitions & Carespace paths
          </ViewButton>
        </div>
      </header>

      <section
        className={cn(
          "min-h-0 flex-1 overflow-hidden rounded-lg border border-border/70 shadow-xs",
          view === "diagram" ? "bg-slate-900" : "bg-background",
        )}
        aria-live="polite"
      >
        {schemaQuery.isPending ? <LoadingState /> : null}
        {schemaQuery.isError ? <ErrorState query={schemaQuery} /> : null}

        {schemaQuery.data && view === "diagram" && erdFrameUrl && canSandboxErd ? (
          <iframe
            key={schemaQuery.data.sha}
            className="block h-full min-h-0 w-full border-0"
            sandbox="allow-scripts allow-same-origin"
            src={erdFrameUrl}
            title="Interactive production database schema"
          />
        ) : null}

        {schemaQuery.data && view === "diagram" && !canSandboxErd ? <ErdOriginErrorState /> : null}

        {schemaQuery.data && view === "definitions" ? (
          <DictionaryExplorer tables={schemaQuery.data.catalog.tables} />
        ) : null}
      </section>
    </DashboardsPageFrame>
  );
}

function CoverageSummary(props: {
  documented: number;
  label: string;
  percent: number;
  total: number;
}) {
  return (
    <div className="min-w-32 rounded-md border border-border/70 bg-muted/30 px-3 py-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[0.68rem] font-semibold tracking-wide text-muted-foreground uppercase">
          {props.label}
        </span>
        <span className="text-xs font-semibold text-foreground">{props.percent}%</span>
      </div>
      <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted">
        <div className="h-full bg-blue-500" style={{ width: `${props.percent}%` }} />
      </div>
      <p className="mt-1 text-[0.68rem] text-muted-foreground">
        {props.documented} of {props.total}
      </p>
    </div>
  );
}

function ViewButton(props: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={props.active}
      className={cn(
        "rounded-sm px-3 py-1.5 text-xs font-medium transition-colors",
        props.active
          ? "bg-background text-foreground shadow-xs"
          : "text-muted-foreground hover:text-foreground",
      )}
      onClick={props.onClick}
    >
      {props.children}
    </button>
  );
}

function DictionaryExplorer({ tables }: { tables: DataDictionaryTable[] }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<DictionaryFilter>("all");
  const defaultTable = tables.find((table) => table.name === "carespace") ?? tables[0] ?? null;
  const [selectedName, setSelectedName] = useState(defaultTable?.name ?? "");

  const filteredTables = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return tables.filter((table) => {
      if (filter === "carespace" && table.carespacePath.kind === "none") return false;
      if (filter === "needs-definition" && table.comment !== null) return false;
      if (!normalizedQuery) return true;
      return (
        table.name.toLowerCase().includes(normalizedQuery) ||
        table.comment?.toLowerCase().includes(normalizedQuery) ||
        table.columns.some(
          (column) =>
            column.name.toLowerCase().includes(normalizedQuery) ||
            column.comment?.toLowerCase().includes(normalizedQuery),
        )
      );
    });
  }, [filter, query, tables]);

  const selectedTable =
    filteredTables.find((table) => table.name === selectedName) ?? filteredTables[0] ?? null;

  return (
    <div className="grid h-full min-h-0 grid-cols-1 lg:grid-cols-[19rem_minmax(0,1fr)]">
      <aside className="flex min-h-0 flex-col border-b border-border bg-muted/20 lg:border-r lg:border-b-0">
        <div className="space-y-2 border-b border-border p-3">
          <label
            className="block text-xs font-semibold text-foreground"
            htmlFor="dictionary-search"
          >
            Find a table, column, or meaning
          </label>
          <input
            id="dictionary-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Try patient, billing, effective date…"
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
          />
          <div className="flex flex-wrap gap-1.5">
            <FilterButton active={filter === "all"} onClick={() => setFilter("all")}>
              All
            </FilterButton>
            <FilterButton active={filter === "carespace"} onClick={() => setFilter("carespace")}>
              Carespace-connected
            </FilterButton>
            <FilterButton
              active={filter === "needs-definition"}
              onClick={() => setFilter("needs-definition")}
            >
              Needs definition
            </FilterButton>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          <p className="px-2 py-1 text-[0.68rem] font-semibold tracking-wide text-muted-foreground uppercase">
            {filteredTables.length} {filteredTables.length === 1 ? "object" : "objects"}
          </p>
          {filteredTables.map((table) => (
            <button
              key={table.name}
              type="button"
              onClick={() => setSelectedName(table.name)}
              className={cn(
                "mb-0.5 flex w-full items-start justify-between gap-2 rounded-md px-2 py-2 text-left",
                selectedTable?.name === table.name
                  ? "bg-blue-50 text-blue-900"
                  : "text-foreground hover:bg-muted",
              )}
            >
              <span className="min-w-0">
                <span className="block truncate font-mono text-xs font-semibold">{table.name}</span>
                <span className="mt-0.5 block truncate text-[0.7rem] text-muted-foreground">
                  {table.comment ?? "Definition pending"}
                </span>
              </span>
              <CarespaceMarker kind={table.carespacePath.kind} />
            </button>
          ))}
        </div>
      </aside>

      <div className="min-h-0 overflow-y-auto">
        {selectedTable ? <TableDefinition table={selectedTable} /> : null}
      </div>
    </div>
  );
}

function FilterButton(props: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={props.active}
      onClick={props.onClick}
      className={cn(
        "rounded-full border px-2.5 py-1 text-[0.7rem] font-medium",
        props.active
          ? "border-blue-300 bg-blue-50 text-blue-800"
          : "border-border bg-background text-muted-foreground hover:text-foreground",
      )}
    >
      {props.children}
    </button>
  );
}

function TableDefinition({ table }: { table: DataDictionaryTable }) {
  return (
    <article className="mx-auto max-w-5xl p-4 md:p-6">
      <div className="flex flex-col justify-between gap-3 border-b border-border pb-5 md:flex-row md:items-start">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-mono text-xl font-semibold text-foreground">{table.name}</h2>
            <CarespaceMarker kind={table.carespacePath.kind} expanded />
          </div>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
            {table.comment ??
              "This object does not have a schema-owned definition yet. Add a PostgreSQL comment in a migration to update this page automatically."}
          </p>
        </div>
        <div className="shrink-0 text-right text-xs text-muted-foreground">
          <div className="font-semibold text-foreground">{table.columns.length} columns</div>
          <div>{table.columns.filter((column) => column.comment !== null).length} defined</div>
        </div>
      </div>

      <CarespacePath table={table} />

      <div className="mt-6 overflow-hidden rounded-lg border border-border">
        <div className="border-b border-border bg-muted/30 px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">Columns</h3>
        </div>
        <div className="divide-y divide-border">
          {table.columns.map((column) => (
            <div
              key={column.name}
              className="grid gap-2 px-4 py-3 md:grid-cols-[13rem_10rem_minmax(0,1fr)]"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <code className="break-all text-xs font-semibold text-foreground">
                    {column.name}
                  </code>
                  {column.isPrimaryKey ? <KeyBadge>PK</KeyBadge> : null}
                  {column.foreignKey ? <KeyBadge>FK</KeyBadge> : null}
                </div>
                {column.foreignKey ? (
                  <p className="mt-1 text-[0.68rem] text-blue-700">
                    → {column.foreignKey.targetTable}.{column.foreignKey.targetColumns.join(", ")}
                  </p>
                ) : null}
              </div>
              <div className="text-xs text-muted-foreground">
                <code>{column.type}</code>
                <span className="ml-2">{column.nullable ? "nullable" : "required"}</span>
              </div>
              <p
                className={cn(
                  "text-sm leading-5",
                  column.comment ? "text-foreground" : "italic text-muted-foreground",
                )}
              >
                {column.comment ?? "Definition pending"}
              </p>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
}

function CarespacePath({ table }: { table: DataDictionaryTable }) {
  const path = table.carespacePath;
  return (
    <section className="mt-5 rounded-lg border border-blue-200 bg-blue-50/60 p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xs font-bold tracking-wide text-blue-900 uppercase">
          Carespace ID path
        </h3>
        <span className="text-[0.68rem] font-medium text-blue-700">
          {path.kind === "root"
            ? "Root object"
            : path.kind === "direct"
              ? "Direct join"
              : path.kind === "indirect"
                ? `${path.steps.length} joins`
                : "No deterministic parent path"}
        </span>
      </div>
      {path.kind === "root" ? (
        <p className="mt-2 font-mono text-xs text-blue-950">carespace.id</p>
      ) : path.steps.length > 0 ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 font-mono text-xs text-blue-950">
          {path.steps.map((step, index) => (
            <span key={`${step.table}-${step.targetTable}-${step.columns.join("-")}`}>
              {index > 0 ? <span className="mr-1.5 text-blue-400">→</span> : null}
              <span>
                {step.table}.{step.columns.join("+")} → {step.targetTable}.
                {step.targetColumns.join("+")}
              </span>
            </span>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-xs leading-5 text-blue-900">
          This table is organization-wide, system-level, or lacks a foreign-key route to
          <code className="mx-1">carespace</code>. Do not assume rows belong to one patient.
        </p>
      )}
    </section>
  );
}

function CarespaceMarker(props: {
  expanded?: boolean;
  kind: DataDictionaryTable["carespacePath"]["kind"];
}) {
  const label =
    props.kind === "root"
      ? "Carespace root"
      : props.kind === "direct"
        ? "Direct"
        : props.kind === "indirect"
          ? "Indirect"
          : "Not scoped";
  return (
    <span
      className={cn(
        "shrink-0 rounded-full border px-1.5 py-0.5 text-[0.62rem] font-semibold",
        props.kind === "none"
          ? "border-border bg-muted text-muted-foreground"
          : "border-blue-200 bg-blue-50 text-blue-700",
      )}
      title={props.expanded ? undefined : label}
    >
      {props.expanded ? label : props.kind === "root" ? "ROOT" : props.kind.toUpperCase()}
    </span>
  );
}

function KeyBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded border border-amber-200 bg-amber-50 px-1 py-0.5 text-[0.58rem] font-bold text-amber-800">
      {children}
    </span>
  );
}

function LoadingState() {
  return (
    <div className="grid h-full place-content-center justify-items-center gap-2 px-8 py-10 text-center text-slate-200">
      <div
        className="size-7 animate-spin rounded-full border-[3px] border-slate-700 border-t-blue-400"
        aria-hidden="true"
      />
      <h2 className="text-lg font-semibold">Preparing the production schema</h2>
      <p className="text-sm text-slate-400">
        Checking the deployed commit and generating the explorer…
      </p>
    </div>
  );
}

function ErrorState({ query }: { query: ReturnType<typeof useQuery> }) {
  return (
    <div className="grid h-full place-content-center justify-items-center gap-2 px-8 py-10 text-center text-slate-200">
      <h2 className="text-lg font-semibold text-red-200">Schema explorer unavailable</h2>
      <p className="text-sm text-slate-400">
        {query.error instanceof Error ? query.error.message : "Schema explorer could not be loaded"}
      </p>
      <button
        type="button"
        className="mt-2 rounded-md border border-slate-500 bg-slate-800 px-3 py-1.5 text-sm text-slate-100 hover:bg-slate-700"
        onClick={() => {
          void query.refetch();
        }}
      >
        Try again
      </button>
    </div>
  );
}

function ErdOriginErrorState() {
  return (
    <div className="grid h-full place-content-center justify-items-center gap-2 px-8 py-10 text-center text-slate-200">
      <h2 className="text-lg font-semibold text-amber-200">Schema map unavailable</h2>
      <p className="max-w-xl text-sm text-slate-400">
        The interactive schema map requires the Operators control plane to use a separate origin.
        Configure the UI&apos;s control-plane URL, then reload this page. The definitions view is
        still available.
      </p>
    </div>
  );
}
